import React, { useState } from 'react';
import { useMachines } from '../context/MachineContext';
import { useTelemetry } from '../context/TelemetryContext';
import { useTickets } from '../context/TicketContext';
import TelemetryChart from '../components/TelemetryChart';
import Toast, { useToast } from '../components/Toast';
import './LiveDashboard.css';

const SENSOR_ICONS: Record<string, string> = {
  temperature: '🌡️', vibration: '📳', rpm: '⚙️',
  pressure: '🔵', humidity: '💧', current: '⚡',
};

const LiveDashboard: React.FC = () => {
  const { machines }                          = useMachines();
  const { sensorLive, heartbeats, activeAlerts, ackAlert } = useTelemetry();
  const { createFromAlert } = useTickets();
  const { toasts, push, remove }              = useToast();
  const [expandedSensor, setExpandedSensor]   = useState<string | null>(null);
  const [alertFilter, setAlertFilter]         = useState<'ALL' | 'WARNING' | 'CRITICAL'>('ALL');

  // Fleet-level KPIs
  const totalSensors  = machines.reduce((n, m) => n + m.sensors.length, 0);
  const activeMachines = Object.values(heartbeats).filter(h => h.status === 'active').length;
  const offlineMachines = Object.values(heartbeats).filter(h => h.status === 'offline').length;
  const breachCount   = Object.values(sensorLive).filter(s => s.status !== 'ok').length;
  const unackedAlerts = activeAlerts.filter(a => !a.acked);

  const visibleAlerts = unackedAlerts.filter(
    a => alertFilter === 'ALL' || a.severity === alertFilter
  );

  const handleAck = (id: string) => {
    ackAlert(id);
    push('Alert acknowledged', 'info');
  };

  const handleCreateTicket = (alert: typeof activeAlerts[0]) => {
    const ticket = createFromAlert(alert);
    if (ticket) push(`Ticket created: ${ticket.title.slice(0, 40)}…`, 'success');
    else        push('Open ticket already exists for this sensor', 'info');
  };

  const handleAckAll = () => {
    unackedAlerts.forEach(a => ackAlert(a.id));
    push(`${unackedAlerts.length} alerts acknowledged`, 'info');
  };

  return (
    <div className="live-dashboard">
      <Toast toasts={toasts} onRemove={remove} />

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>📡 Live Telemetry</h1>
          <p className="page-subtitle">Real-time sensor data · updates every 2s</p>
        </div>
        <div className="live-indicator">
          <span className="pulse-ring" />
          <span className="live-label">LIVE</span>
        </div>
      </div>

      {/* Fleet KPIs */}
      <div className="fleet-kpis">
        {[
          { label: 'Machines',       value: machines.length,  icon: '🏭', color: '#00d4ff' },
          { label: 'Active',         value: activeMachines,   icon: '🟢', color: '#34d399' },
          { label: 'Offline',        value: offlineMachines,  icon: '🔴', color: '#f87171' },
          { label: 'Total Sensors',  value: totalSensors,     icon: '📡', color: '#a78bfa' },
          { label: 'Threshold Breach', value: breachCount,    icon: '⚠️', color: '#fbbf24' },
          { label: 'Active Alerts',  value: unackedAlerts.length, icon: '🔔', color: '#f97316' },
        ].map((k, i) => (
          <div className="fleet-kpi card" key={i}>
            <span className="fkpi-icon">{k.icon}</span>
            <span className="fkpi-value" style={{ color: k.color }}>{k.value}</span>
            <span className="fkpi-label">{k.label}</span>
          </div>
        ))}
      </div>

      <div className="live-body">
        {/* Left — machine sensor grid */}
        <div className="machines-telemetry">
          {machines.map(m => {
            const hb = heartbeats[m.machineId];
            const machineStatus = hb?.status ?? 'offline';
            const machineAlerts = unackedAlerts.filter(a => a.machineId === m.machineId);

            return (
              <div key={m.machineId} className={`machine-telem-card card status-${machineStatus}`}>
                {/* Machine header */}
                <div className="mtc-header">
                  <div className="mtc-title">
                    <span className={`heartbeat-dot dot-${machineStatus}`} />
                    <strong>{m.name}</strong>
                    <span className="mtc-type">{m.type}</span>
                  </div>
                  <div className="mtc-meta">
                    <span className="mtc-location">📍 {m.location}</span>
                    {machineAlerts.length > 0 && (
                      <span className="mtc-alert-badge">{machineAlerts.length} alert{machineAlerts.length > 1 ? 's' : ''}</span>
                    )}
                    {machineStatus === 'offline' && (
                      <span className="badge-red">● Offline</span>
                    )}
                    {machineStatus === 'idle' && (
                      <span className="badge-yellow">○ Idle</span>
                    )}
                    {machineStatus === 'active' && (
                      <span className="badge-green">● Active</span>
                    )}
                  </div>
                </div>

                {/* Sensor rows */}
                {m.sensors.length === 0 ? (
                  <p className="no-sensors-msg">No sensors configured</p>
                ) : (
                  <div className="sensor-telem-grid">
                    {m.sensors.map(s => {
                      const live    = sensorLive[s.sensorId];
                      const val     = live?.currentValue ?? s.currentValue ?? 0;
                      const status  = live?.status ?? 'ok';
                      const history = live?.history ?? [];
                      const isExpanded = expandedSensor === s.sensorId;

                      return (
                        <div
                          key={s.sensorId}
                          className={`sensor-telem-row status-${status} ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => setExpandedSensor(isExpanded ? null : s.sensorId)}
                        >
                          <div className="str-left">
                            <span className="str-icon">{SENSOR_ICONS[s.type] || '📊'}</span>
                            <div className="str-info">
                              <span className="str-name">{s.name}</span>
                              <span className="str-type">{s.type} · {s.unit}</span>
                            </div>
                          </div>

                          <div className="str-sparkline">
                            <TelemetryChart
                              sensor={s} history={history}
                              status={status} height={36} sparkline
                            />
                          </div>

                          <div className="str-right">
                            <span className={`str-value val-${status}`}>
                              {val.toFixed(1)} {s.unit}
                            </span>
                            <span className="str-range">
                              {s.minThreshold}–{s.maxThreshold}
                            </span>
                            {status !== 'ok' && (
                              <span className={`str-badge ${status === 'critical' ? 'badge-red' : 'badge-yellow'}`}>
                                {status === 'critical' ? '🔴' : '🟡'} {status.toUpperCase()}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Expanded sensor chart */}
                {expandedSensor && m.sensors.find(s => s.sensorId === expandedSensor) && (() => {
                  const s    = m.sensors.find(s => s.sensorId === expandedSensor)!;
                  const live = sensorLive[s.sensorId];
                  return (
                    <div className="expanded-chart">
                      <div className="expanded-chart-header">
                        <span>{SENSOR_ICONS[s.type]} {s.name} — 30-point history</span>
                        <button className="close-chart" onClick={e => { e.stopPropagation(); setExpandedSensor(null); }}>✕</button>
                      </div>
                      <TelemetryChart
                        sensor={s}
                        history={live?.history ?? []}
                        status={live?.status ?? 'ok'}
                        height={180}
                      />
                    </div>
                  );
                })()}
              </div>
            );
          })}

          {machines.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: 48 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⚙️</div>
              <h3>No machines configured</h3>
              <p style={{ color: 'var(--text-muted)', marginTop: 8 }}>
                Go to <strong>Machines</strong> to add machines and sensors first.
              </p>
            </div>
          )}
        </div>

        {/* Right — alert feed */}
        <div className="alert-sidebar">
          <div className="alert-sidebar-header">
            <h3>🔔 Active Alerts</h3>
            <div className="alert-sidebar-actions">
              <select
                className="alert-filter-select"
                value={alertFilter}
                onChange={e => setAlertFilter(e.target.value as any)}
              >
                <option value="ALL">All ({unackedAlerts.length})</option>
                <option value="CRITICAL">Critical ({unackedAlerts.filter(a => a.severity === 'CRITICAL').length})</option>
                <option value="WARNING">Warning ({unackedAlerts.filter(a => a.severity === 'WARNING').length})</option>
              </select>
              {unackedAlerts.length > 0 && (
                <button className="btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }} onClick={handleAckAll}>
                  Ack All
                </button>
              )}
            </div>
          </div>

          <div className="alert-feed-list">
            {visibleAlerts.length === 0 ? (
              <div className="no-alerts">
                <span>✅</span>
                <p>All sensors within thresholds</p>
              </div>
            ) : (
              visibleAlerts.slice(0, 30).map(a => (
                <div key={a.id} className={`alert-feed-item sev-${a.severity.toLowerCase()}`}>
                  <div className="afi-top">
                    <span className="afi-sev">{a.severity === 'CRITICAL' ? '🔴' : '🟡'} {a.severity}</span>
                    <span className="afi-time">{new Date(a.ts).toLocaleTimeString()}</span>
                  </div>
                  <div className="afi-machine">{a.machineName}</div>
                  <div className="afi-detail">
                    {a.sensorName}: <strong>{a.value.toFixed(1)}</strong>
                    {' '}{a.direction === 'high' ? '▲ above' : '▼ below'} {a.threshold}
                  </div>
                  <button className="afi-ack" onClick={() => handleAck(a.id)}>✓ Ack</button>
                  <button className="afi-ticket" onClick={() => handleCreateTicket(a)}>🎫 Ticket</button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveDashboard;
